import { useMemo } from "react";
import { useEffect, useState } from "react";
import { CoachAppSetting, CoachSettings, SocialNetwork, SocialNetworksId } from "../../../interfaces";
import { PlayerCardsContextData } from "./PlayerCardsContext";
import { useSocialNetworks } from "../../../utils";
import { PlayersServices, SettingsServices } from "../../../services";
import { UserContextData } from "./UserContext";

export type SocialNetworkSettings = {
  [K in SocialNetworksId]?: {
    isPermitted?: boolean;
    socialNetwork?: SocialNetwork;
  };
};

export interface SocialNetworkSettingsContextData {
  socialNetworkSettings: SocialNetworkSettings;
}

export const InitialSocialNetworkSettingsContextData: SocialNetworkSettingsContextData = {
  socialNetworkSettings: {},
};

export const SocialNetworkSettingsContext = (
  PlayerCards: PlayerCardsContextData,
  User: UserContextData
): SocialNetworkSettingsContextData => {
  const [coachAppSettings, setCoachAppSettings] = useState<CoachAppSetting>();
  const { displayTikTok, displayGoogle, displayZillow } = useSocialNetworks();
  const [socialNetworkSettings, setSocialNetworkSettings] = useState<SocialNetworkSettings>({});
  const [playerSocialNetworks, setPlayerSocialNetworks] = useState<{ [key: number]: SocialNetwork[] }>({});

  const playerId = User.playerId || PlayerCards.selectedPlayerCard?.playerId;

  useEffect(() => {
    if (!playerId) return;

    setPlayerSocialNetworks(prev => {
      if (User.isPlayer && User.socialNetworks) {
        return {
          ...prev,
          [playerId]: User.socialNetworks,
        };
      } else if (!prev[playerId]) {
        PlayersServices.get(playerId).then(({ profile: { socialNetworks } }) => {
          setPlayerSocialNetworks(prev => {
            return {
              ...prev,
              [playerId]: socialNetworks || [],
            };
          });
        });
      }
      return prev;
    });
  }, [playerId]);

  const socialNetworks: SocialNetwork[] = useMemo(() => {
    if (!playerId) return [];
    return playerSocialNetworks[playerId] || [];
  }, [playerSocialNetworks, playerId]);

  useEffect(() => {
    if (!User.coachId) return;

    SettingsServices.getAppSettingsByCoachId(User.coachId.toString()).then(setCoachAppSettings);
  }, []);

  useEffect(() => {
    if (!coachAppSettings) return;

    const settingsHash = Object.fromEntries(
      coachAppSettings.settings.map(setting => [setting.appSettingId, (setting.status || "ACTIVE") === "ACTIVE"])
    );
    const networkIds: ("FB" | "IG" | "LI" | "TT" | "TW" | "GL" | "ZL")[] = ["FB", "IG", "LI", "TT", "TW", "GL", "ZL"];
    const socialNetworkSettings: SocialNetworkSettings = {};
    networkIds.forEach(networkId => {
      let isPermitted = settingsHash[CoachSettings[`POST_IN_${networkId}`]];
      if (networkId === "TT") {
        isPermitted = isPermitted && displayTikTok;
      } else if (networkId === "GL") {
        isPermitted = isPermitted && displayGoogle;
      } else if (networkId === "ZL") {
        isPermitted = isPermitted && displayZillow;
      }
      const socialNetwork = socialNetworks.find(sn => sn.networkId === networkId);
      socialNetworkSettings[networkId] = { isPermitted, socialNetwork };
    });
    setSocialNetworkSettings(socialNetworkSettings);
  }, [coachAppSettings, socialNetworks, displayTikTok, displayGoogle, displayZillow]);

  return {
    socialNetworkSettings,
  };
};
